// ATLAS dropdown — the TELOS cascade-table control on mobile: a field showing
// the current pick ("N selected" for multi), tap to open an inline list of
// options (radio / checkbox), with a "Custom…" row + ⊕ at the bottom. Custom
// options (typed or AI-written) carry a pencil (rename in place) and a trash.
import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { Check, ChevronDown, ChevronUp, Pencil, PlusCircle, Trash2 } from 'lucide-react-native';
import { useTheme, makeThemedStyles } from '../../v10/theme';

export interface DropdownOption {
  key: string;
  label: string;
  /** Custom (not a preset): shows rename + delete when the menu has onEdit / onDelete. */
  editable?: boolean;
  /** Text to edit when it differs from the label (e.g. a label with a deity tag appended). */
  editText?: string;
  /** Label colour, when the option's kind is colour-coded (e.g. principles: written yellow, found green). */
  color?: string;
}

export function AtlasDropdown({
  placeholder,
  options,
  selected,
  multi = false,
  open,
  onOpen,
  onPick,
  onAddCustom,
  customPlaceholder = 'Custom…',
}: {
  placeholder: string;
  options: DropdownOption[];
  /** Selected option keys. */
  selected: string[];
  multi?: boolean;
  open: boolean;
  onOpen: (open: boolean) => void;
  /** Single: the chosen key. Multi: the toggled key. */
  onPick: (key: string) => void;
  /** Omit to hide the custom row. */
  onAddCustom?: (text: string) => void;
  customPlaceholder?: string;
}) {
  const styles = useStyles();
  const { t } = useTheme();
  const chosen = options.filter((o) => selected.includes(o.key));
  const summary = multi
    ? chosen.length === 0
      ? null
      : chosen.length === 1
      ? chosen[0].label
      : `${chosen.length} selected`
    : chosen[0]?.label ?? null;
  const Chev = open ? ChevronUp : ChevronDown;

  return (
    <View>
      <Pressable onPress={() => onOpen(!open)} style={[styles.field, open && { borderColor: t.gold }]} accessibilityRole="button">
        <Text style={[styles.value, !summary && styles.placeholder]} numberOfLines={2}>
          {summary ?? placeholder}
        </Text>
        <Chev size={18} color={t.ivorySoft} />
      </Pressable>
      {open ? (
        <DropdownMenu options={options} selected={selected} multi={multi} onPick={onPick} onClose={() => onOpen(false)} onAddCustom={onAddCustom} customPlaceholder={customPlaceholder} />
      ) : null}
    </View>
  );
}

/** The open list: radio / checkbox options + optional custom row. */
export function DropdownMenu({
  options,
  selected,
  multi = false,
  onPick,
  onClose,
  onAddCustom,
  customPlaceholder = 'Custom…',
  onEdit,
  onDelete,
}: {
  options: DropdownOption[];
  selected: string[];
  multi?: boolean;
  onPick: (key: string) => void;
  onClose: () => void;
  onAddCustom?: (text: string) => void;
  customPlaceholder?: string;
  /** Rename an editable option (key, new label). */
  onEdit?: (key: string, label: string) => void;
  /** Delete an editable option. */
  onDelete?: (key: string) => void;
}) {
  const styles = useStyles();
  const { t } = useTheme();
  const [custom, setCustom] = useState('');
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  const saveEdit = () => {
    if (!editing || !onEdit) return;
    const v = editing.text.trim();
    const o = options.find((x) => x.key === editing.key);
    const was = o?.editText ?? o?.label;
    if (v && v !== was) onEdit(editing.key, v);
    setEditing(null);
  };
  const add = () => {
    const v = custom.trim();
    if (!v || !onAddCustom) return;
    onAddCustom(v);
    setCustom('');
    if (!multi) onClose();
  };
  return (
        <View style={styles.menu}>
          {options.map((o) => {
            const on = selected.includes(o.key);
            if (editing?.key === o.key) {
              return (
                <View key={o.key} style={styles.row}>
                  <TextInput
                    style={styles.customInput}
                    value={editing.text}
                    onChangeText={(text) => setEditing({ key: o.key, text })}
                    onSubmitEditing={saveEdit}
                    autoFocus
                    maxLength={160}
                  />
                  <Pressable onPress={saveEdit} hitSlop={8} accessibilityLabel="Save">
                    <Check size={20} color={t.gold} strokeWidth={2.6} />
                  </Pressable>
                </View>
              );
            }
            const tools = o.editable && (onEdit || onDelete);
            return (
              <Pressable
                key={o.key}
                style={styles.row}
                onPress={() => {
                  onPick(o.key);
                  if (!multi) onClose();
                }}
              >
                <View style={[styles.box, !multi && styles.radio, on && { backgroundColor: t.gold, borderColor: t.gold }]}>
                  {on ? <Check size={13} color={t.onGold} strokeWidth={3} /> : null}
                </View>
                <Text style={[styles.rowText, o.color ? { color: o.color } : null]}>{o.label}</Text>
                {tools && onEdit ? (
                  <Pressable onPress={() => setEditing({ key: o.key, text: o.editText ?? o.label })} hitSlop={8} accessibilityLabel={`Edit ${o.label}`}>
                    <Pencil size={16} color={t.ivorySoft} />
                  </Pressable>
                ) : null}
                {tools && onDelete ? (
                  <Pressable onPress={() => onDelete(o.key)} hitSlop={8} accessibilityLabel={`Delete ${o.label}`}>
                    <Trash2 size={16} color={t.ivorySoft} />
                  </Pressable>
                ) : null}
              </Pressable>
            );
          })}
          {onAddCustom ? (
          <View style={styles.customRow}>
            <TextInput
              style={styles.customInput}
              value={custom}
              onChangeText={setCustom}
              onSubmitEditing={add}
              placeholder={customPlaceholder}
              placeholderTextColor={t.ivoryFaint}
              maxLength={160}
            />
            <Pressable onPress={add} hitSlop={8} accessibilityLabel="Add custom">
              <PlusCircle size={22} color={custom.trim() ? t.gold : t.ivoryFaint} />
            </Pressable>
          </View>
          ) : null}
        </View>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    field: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      minHeight: 46,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: c.inputFill,
    },
    value: { flex: 1, fontSize: 15, color: t.ivory },
    placeholder: { color: t.ivoryFaint },
    menu: {
      marginTop: 6,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: t.navyRaised,
      paddingVertical: 6,
    },
    row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 14, paddingVertical: 10 },
    box: { width: 22, height: 22, borderRadius: 6, borderWidth: 1.5, borderColor: t.gold, alignItems: 'center', justifyContent: 'center' },
    radio: { borderRadius: 11 },
    rowText: { flex: 1, fontSize: 14.5, color: t.ivory, lineHeight: 20 },
    customRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 4,
      marginHorizontal: 10,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: t.lineFaint,
    },
    customInput: {
      flex: 1,
      fontSize: 14,
      color: t.ivory,
      paddingHorizontal: 12,
      paddingVertical: 9,
      borderRadius: 10,
      backgroundColor: c.inputFill,
    },
  }),
);
